import classNames from "classnames";
import React, { Component, ComponentType, FormEvent } from "react";
import FieldError from "Shared/Form/FieldError";
import Input from "Shared/Form/Input";
import Label from "Shared/Form/Label";
import styled from "styled-components";
import { isEmpty } from "src/utils/object";
import { ValidatorFunction } from "./validators";

const FormRow = styled("div")`
    margin: 0 -10px;
`;

interface Validator {
    message: string;
    validator: ValidatorFunction;
}

export interface Field {
    initialValue?: string | null;
    input?: ComponentType<any>;
    inputProps?: any;
    label: string;
    validators: Array<Validator | ValidatorFunction>;
    wrapper: ComponentType<any>;
    onChange?: (target: any) => void;
}

export interface Fields {
    [index: string]: Field;
}

export type FormDataValue = string | Date | boolean;

export interface FormData {
    [index: string]: FormDataValue;
}

export interface FormProps {
    className?: string;
    fields: { [index: string]: Field };
    fieldsPerRow: string[][];
    onChange?: (data: any) => void;
}

interface FormState {
    formData: FormData;
    errors: {
        [index: string]: string;
    };
}

class Form extends Component<FormProps, FormState> {
    constructor(props: FormProps) {
        super(props);

        const fieldsNames = Object.keys(props.fields);
        const formData = {};
        fieldsNames.forEach((fieldName) => {
            formData[fieldName] = props.fields[fieldName].initialValue || "";
        });

        this.state = {
            errors: {},
            formData,
        };
    }

    public isFormValid = (): boolean => {
        const { formData } = this.state;
        const fieldsNames = Object.keys(formData);
        const formErrors = {};

        fieldsNames.forEach((fieldName) => {
            const fieldErrors = this.getFieldErrors(fieldName, formData[fieldName]);
            if (fieldErrors) {
                formErrors[fieldName] = fieldErrors;
            } else {
                delete formErrors[fieldName];
            }
        });

        if (!isEmpty(formErrors)) {
            this.setState({ errors: formErrors });
            return false;
        } else {
            this.setState({ errors: {} });
            return true;
        }
    };

    public getFormData = (): FormData => {
        return { ...this.state.formData };
    };

    public render() {
        const { className } = this.props;

        return <form className={classNames("Form", className)}>{this.renderFormRows()}</form>;
    }

    public renderFormRows() {
        return this.props.fieldsPerRow.map((row, index) => (
            <FormRow key={`form-row-${index}`}>{row.map((fieldName) => this.renderFormField(fieldName))}</FormRow>
        ));
    }

    public renderFormField(fieldName: string) {
        const field = this.props.fields[fieldName];
        const Wrapper = field.wrapper || "div";
        const InputComponent = field.input || Input;
        const { errors } = this.state;

        return (
            <Wrapper key={`field-wrapper-${fieldName}`}>
                <Label>
                    {field.label}
                    <InputComponent {...this.getInputProps(fieldName)} />
                    <FieldError>{errors[fieldName]}</FieldError>
                </Label>
            </Wrapper>
        );
    }

    private getInputProps = (inputName: string) => {
        const { fields } = this.props;
        const { formData, errors } = this.state;

        return {
            error: !!errors[inputName],
            hasError: true,
            hasLabel: true,
            name: inputName,
            onBlur: this.handleInputBlur,
            onChange: this.handleInputChange,
            type: "text",
            value: formData[inputName],
            ...fields[inputName].inputProps,
        };
    };

    private handleInputChange = (event: FormEvent<HTMLInputElement>) => {
        const { onChange, fields } = this.props;
        const target = event.target as HTMLInputElement;
        const value: string = target.value;
        const name: string = target.name;

        this.setState(
            {
                formData: { ...this.state.formData, [name]: value },
            },
            () => {
                onChange && onChange(this.state.formData);

                const field = fields[name];

                if (field) {
                    field.onChange && field.onChange(target);
                }
            }
        );
    };

    private handleInputBlur = (event: FormEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement;
        const value: string = target.value;
        const name: string = target.name;

        this.setState({
            errors: {
                ...this.state.errors,
                [name]: this.getFieldErrors(name, value),
            },
        });
    };

    private getFieldErrors = (name: string, value: FormDataValue): string => {
        const inputValidators = this.props.fields[name].validators;
        const errors: string[] = [];

        inputValidators.forEach((validator: any) => {
            const errorMessage =
                typeof validator === "function" ? validator(value) : validator.validator(value, validator.message);
            if (errorMessage) {
                errors.push(errorMessage);
            }
        });

        return errors.length ? errors.join(", ") : "";
    };
}

export default Form;
